import { useState, useRef, useCallback, useEffect } from "react";
import { Microphone, Stop } from "@phosphor-icons/react";

const SpeechRecognition = typeof window !== "undefined"
  ? window.SpeechRecognition || window.webkitSpeechRecognition
  : null;

export function VoiceDictationButton({ onTranscript, disabled = false, className = "" }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const supported = !!SpeechRecognition;

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (!SpeechRecognition) return;
    if (recognitionRef.current) { stop(); return; }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          transcript += event.results[i][0].transcript;
        }
      }
      if (transcript.trim()) onTranscript(transcript.trim());
    };

    recognition.onerror = () => { stop(); };
    recognition.onend = () => { setListening(false); recognitionRef.current = null; };

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }, [onTranscript, stop]);

  useEffect(() => { return () => { if (recognitionRef.current) recognitionRef.current.stop(); }; }, []);

  if (!supported) return null;

  return (
    <button
      type="button"
      onClick={listening ? stop : start}
      disabled={disabled}
      title={listening ? "Stop dictation" : "Voice input"}
      className={`inline-flex items-center justify-center rounded-lg transition-colors ${
        listening
          ? "bg-red-500/15 text-red-400 hover:bg-red-500/25"
          : "bg-[var(--bg-base)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"} ${className}`}
      data-testid="voice-dictation-btn"
    >
      {listening ? (
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
          <Stop size={16} weight="fill" />
        </span>
      ) : (
        <Microphone size={16} />
      )}
    </button>
  );
}
